import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, CheckSquare, Calendar, Users, Settings, LogOut,
  Menu, X, ChevronDown, Home, FileText, BarChart3, Building,
  HardHat, Receipt, TrendingUp, CalendarDays, FolderKanban, FolderOpen,
  Package, Wrench, Ticket, MessagesSquare, Check, Mail, CalendarCheck, Link2, Bell, MapPin, Euro, Megaphone, Bot, Code, Store, Palette, Image, Star, Lock, Sparkles
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import NotificationCenter from '../../components/notifications/NotificationCenter';

import { API_URL } from '../../config/api';

// Composant Logo Cockpit Pro
function LogoCockpitPro({ className = "h-10", showText = true }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <svg viewBox="0 0 40 40" className="h-full w-auto" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Dashboard/Cockpit icon */}
        <rect x="4" y="8" width="32" height="24" rx="3" fill="#3B82F6"/>
        <rect x="7" y="11" width="12" height="8" rx="1" fill="#60A5FA"/>
        <rect x="21" y="11" width="12" height="8" rx="1" fill="#60A5FA"/>
        <rect x="7" y="21" width="26" height="8" rx="1" fill="#1D4ED8"/>
        <circle cx="20" cy="25" r="2" fill="#60A5FA"/>
      </svg>
      {showText && (
        <span className="font-bold text-xl">
          <span className="text-blue-600">Cockpit</span>
          <span className="text-blue-800">Pro</span>
        </span>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// CONFIGURATION DES MODULES PAYANTS
// ═══════════════════════════════════════════════════════════════════════════════
const SUBSCRIPTION_MODULES = {
  chantiers: {
    name: 'Module Chantiers',
    price: '29€/mois',
    unlockPath: '/espace-client/modules/chantiers',
    description: 'Gestion chantiers, Pointage GPS, Comptes Rendus, Rentabilité'
  },
  boutiquepro: {
    name: 'Boutique Pro',
    price: '19€/mois',
    unlockPath: '/espace-client/boutique',
    description: 'Vente de produits, connexion Marketplace'
  },
  rendezvous: {
    name: 'Module Rendez-vous',
    price: '19€/mois',
    unlockPath: '/espace-client/modules/rendezvous',
    description: 'Agenda services, créneaux en ligne, rappels automatiques'
  }
};

const clientNavItems = [
  { icon: Home, label: 'Tableau de bord', path: '/espace-client/dashboard' },
  { icon: MessagesSquare, label: 'Tchat Équipe', path: '/espace-client/team-chat', badge: true, highlight: true },
  { icon: Mail, label: 'Emails', path: '/espace-client/emails', badge: true },
  { icon: CalendarCheck, label: 'Agenda partagé', path: '/espace-client/shared-calendar' },
  { icon: MessageSquare, label: 'Messages', path: '/espace-client/messages', badge: true },
  { icon: CheckSquare, label: 'Tâches', path: '/espace-client/tasks' },
  { icon: Calendar, label: 'Agenda', path: '/espace-client/calendar' },
  { icon: Users, label: 'Mon équipe', path: '/espace-client/team', showAssistants: true, hideForAssistant: true },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CRM CORE - Universel pour tous les pros
  // ═══════════════════════════════════════════════════════════════════════════
  { type: 'divider', label: '📊 CRM', isBrand: true },
  { icon: Sparkles, label: 'Nouvelles opportunités', path: '/espace-client/crm/devis?tab=opportunities', isOpportunity: true, highlight: true },
  { icon: BarChart3, label: 'Vue d\'ensemble', path: '/espace-client/crm' },
  { icon: Users, label: 'Prospects', path: '/espace-client/crm/prospects' },
  { icon: FolderKanban, label: 'Pipeline', path: '/espace-client/crm/pipeline' },
  { icon: Building, label: 'Clients', path: '/espace-client/crm/clients' },
  { icon: FileText, label: 'Devis', path: '/espace-client/crm/devis' },
  { icon: Receipt, label: 'Factures', path: '/espace-client/crm/factures' },
  { icon: Wrench, label: 'Prestations', path: '/espace-client/crm/prestations' },
  { icon: Ticket, label: 'SAV', path: '/espace-client/crm/tickets' },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MODULE CHANTIERS - Add-on BTP (payant)
  // ═══════════════════════════════════════════════════════════════════════════
  { type: 'divider', label: '🏗️ Chantiers', isBrand: true, module: 'chantiers' },
  { icon: HardHat, label: 'Mes Chantiers', path: '/espace-client/chantiers', highlight: true, module: 'chantiers', locked: true },
  { icon: CalendarDays, label: 'Planning', path: '/espace-client/chantiers/planning', module: 'chantiers', locked: true },
  { icon: MapPin, label: 'Pointage GPS', path: '/espace-client/pointage', highlight: true, module: 'chantiers', locked: true },
  { icon: TrendingUp, label: 'Rentabilité', path: '/espace-client/chantiers/rentabilite', module: 'chantiers', locked: true },
  { icon: FolderOpen, label: 'Documents', path: '/espace-client/chantiers/documents', module: 'chantiers', locked: true },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // BOUTIQUE PRO - Module Vente de Produits (payant)
  // ═══════════════════════════════════════════════════════════════════════════
  { type: 'divider', label: '🏪 Ma Vitrine', isBrand: true },
  { icon: Store, label: 'Ma Boutique', path: '/espace-client/ma-boutique', highlight: true, isNew: true },
  { icon: Package, label: 'Mes Produits', path: '/espace-client/crm/produits' },
  { icon: Wrench, label: 'Mes Services', path: '/espace-client/crm/prestations' },
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MARKETING & OUTILS COMMUNS
  // ═══════════════════════════════════════════════════════════════════════════
  { type: 'divider', label: 'Marketing' },
  { icon: Megaphone, label: 'Tableau de bord', path: '/espace-client/marketing', highlight: true },
  { icon: CalendarDays, label: 'Calendrier', path: '/espace-client/marketing/calendar' },
  { icon: MessagesSquare, label: 'Engagement', path: '/espace-client/marketing/engagement', badge: true },
  { icon: Bot, label: 'Agents IA', path: '/espace-client/marketing/ai-agents', highlight: true },
  { icon: Link2, label: 'Comptes sociaux', path: '/espace-client/marketing/accounts' },
  
  { type: 'divider', label: 'Agent IA Vendeur' },
  { icon: Bot, label: 'Mon Agent', path: '/espace-client/ai-vendor', highlight: true, isNew: true },
  { icon: Package, label: 'Catalogue', path: '/espace-client/ai-vendor/catalog' },
  { icon: MessageSquare, label: 'Conversations', path: '/espace-client/ai-vendor/conversations', badge: true },
  { icon: Code, label: 'Widget', path: '/espace-client/ai-vendor/widget', isNew: true },
  { icon: Settings, label: 'Configuration', path: '/espace-client/ai-vendor/settings' },
  
  { type: 'divider', label: 'Paramètres' },
  { icon: Euro, label: 'Taux horaires', path: '/espace-client/settings/hourly-rates', hideForAssistant: true },
  { icon: Link2, label: 'Comptes connectés', path: '/espace-client/settings/connected-accounts', hideForAssistant: true },
  { icon: Bell, label: 'Notifications', path: '/espace-client/settings/notifications' },
];

const adminNavItems = [
  { icon: Home, label: 'Tableau de bord', path: '/espace-client/dashboard' },
  { icon: MessagesSquare, label: 'Tchat Équipe', path: '/espace-client/team-chat', badge: true, highlight: true },
  { icon: MessageSquare, label: 'Messages', path: '/espace-client/messages', badge: true },
  { icon: CheckSquare, label: 'Tâches', path: '/espace-client/tasks' },
  { icon: Calendar, label: 'Agenda', path: '/espace-client/calendar' },
  
  // CRM Core - Universel
  { type: 'divider', label: '📊 CRM', isBrand: true },
  { icon: Sparkles, label: 'Nouvelles opportunités', path: '/espace-client/crm/devis?tab=opportunities', isOpportunity: true, highlight: true },
  { icon: BarChart3, label: 'Vue d\'ensemble', path: '/espace-client/crm' },
  { icon: Users, label: 'Prospects', path: '/espace-client/crm/prospects' },
  { icon: FolderKanban, label: 'Pipeline', path: '/espace-client/crm/pipeline' },
  { icon: Building, label: 'Clients', path: '/espace-client/crm/clients' },
  { icon: FileText, label: 'Devis', path: '/espace-client/crm/devis' },
  { icon: Receipt, label: 'Factures', path: '/espace-client/crm/factures' },
  { icon: Wrench, label: 'Prestations', path: '/espace-client/crm/prestations' },
  { icon: Ticket, label: 'SAV', path: '/espace-client/crm/tickets' },
  
  // Module Chantiers - BTP
  { type: 'divider', label: '🏗️ Chantiers', isBrand: true },
  { icon: HardHat, label: 'Mes Chantiers', path: '/espace-client/chantiers', highlight: true },
  { icon: CalendarDays, label: 'Planning', path: '/espace-client/chantiers/planning' },
  { icon: MapPin, label: 'Pointage GPS', path: '/espace-client/pointage', highlight: true },
  { icon: TrendingUp, label: 'Rentabilité', path: '/espace-client/chantiers/rentabilite' },
  { icon: FolderOpen, label: 'Documents', path: '/espace-client/chantiers/documents' },
  
  // Boutique Pro - Produits
  { type: 'divider', label: '🛒 Boutique Pro', isBrand: true },
  { icon: Store, label: 'Ma Boutique', path: '/espace-client/boutique', highlight: true },
  { icon: Package, label: 'Mes Produits', path: '/espace-client/crm/produits' },
  
  // Marketing
  { type: 'divider', label: 'Marketing' },
  { icon: Megaphone, label: 'Tableau de bord', path: '/espace-client/marketing', highlight: true },
  { icon: CalendarDays, label: 'Calendrier', path: '/espace-client/marketing/calendar' },
  { icon: MessagesSquare, label: 'Engagement', path: '/espace-client/marketing/engagement', badge: true },
  { icon: Bot, label: 'Agents IA', path: '/espace-client/marketing/ai-agents', highlight: true },
  { icon: Link2, label: 'Comptes sociaux', path: '/espace-client/marketing/accounts' },
  
  // Admin
  { type: 'divider', label: 'Administration' },
  { icon: Users, label: 'Utilisateurs', path: '/espace-client/users' },
  { icon: FileText, label: 'Blog', path: '/espace-client/blog' },
];

export default function ClientLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [assistants, setAssistants] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [activeClientId, setActiveClientId] = useState(null);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [opportunitiesCount, setOpportunitiesCount] = useState(0);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // GESTION DES ABONNEMENTS - Modules débloqués
  // ═══════════════════════════════════════════════════════════════════════════
  const [activeSubscriptions, setActiveSubscriptions] = useState([]);
  
  // Vérifier si un module est débloqué
  const isModuleUnlocked = (moduleName) => {
    // En mode démo, tout est débloqué
    if (localStorage.getItem('demo_mode') === 'true') return true;
    // Vérifier les abonnements actifs
    return activeSubscriptions.includes(moduleName);
  };

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('client_token');
    const storedUser = localStorage.getItem('client_user');

    if (!token || !storedUser) {
      navigate('/espace-client');
      return;
    }

    try {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      
      // Récupérer les abonnements actifs
      fetchActiveSubscriptions(token);
      
      // For assistants, fetch their assigned clients
      if (parsedUser.role === 'assistant') {
        fetchAssignedClients(token);
      } else {
        // For clients, fetch their assistants
        fetchAssistants(parsedUser.id, token);
      }
    } catch {
      navigate('/espace-client');
    }
  }, [navigate]);

  const fetchAssignedClients = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/client/assistant/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignedClients(data);
        // Set first client as active by default
        if (data.length > 0) {
          const savedClientId = localStorage.getItem('active_client_id');
          if (savedClientId && data.some(c => c.id === savedClientId)) {
            setActiveClientId(savedClientId);
          } else {
            setActiveClientId(data[0].id);
            localStorage.setItem('active_client_id', data[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching assigned clients:', error);
    }
  };
  
  // Récupérer les abonnements actifs de l'utilisateur
  const fetchActiveSubscriptions = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/addons/my-subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Extraire les noms de modules actifs
        const activeModules = data.subscriptions
          ?.filter(s => s.status === 'active')
          ?.map(s => s.module_id) || [];
        setActiveSubscriptions(activeModules);
      }
    } catch (error) {
      // En cas d'erreur, on laisse vide (tout verrouillé par défaut)
      console.log('Subscriptions not available');
    }
  };

  const handleClientChange = (clientId) => {
    setActiveClientId(clientId);
    localStorage.setItem('active_client_id', clientId);
    setShowClientSelector(false);
    // Refresh the page to reload data for new client context
    window.location.reload();
  };

  const fetchAssistants = async (userId, token) => {
    try {
      const response = await fetch(`${API_URL}/api/client/users/${userId}/assistants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssistants(data);
      }
    } catch (error) {
      console.error('Error fetching assistants:', error);
    }
  };

  useEffect(() => {
    // Fetch unread messages count
    const fetchUnreadCount = async () => {
      const token = localStorage.getItem('client_token');
      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/api/client/conversations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const conversations = await response.json();
          const count = conversations.reduce((acc, conv) => acc + (conv.unread_count || 0), 0);
          setUnreadCount(count);
        }
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, []);

  // Fetch opportunities count
  useEffect(() => {
    const fetchOpportunitiesCount = async () => {
      try {
        const response = await fetch(`${API_URL}/api/artisans/public-devis-requests`);
        if (response.ok) {
          const data = await response.json();
          setOpportunitiesCount(data.devis_requests?.length || 0);
        }
      } catch (error) {
        console.log('Could not fetch opportunities count');
      }
    };

    fetchOpportunitiesCount();
    const interval = setInterval(fetchOpportunitiesCount, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('client_token');
    localStorage.removeItem('client_user');
    localStorage.removeItem('active_client_id');
    toast.success('Deconnexion reussie');
    navigate('/espace-client');
  };

  const isAssistant = user?.role === 'assistant';
  const baseNavItems = user?.role === 'admin' ? adminNavItems : clientNavItems;
  // Filter out items that should be hidden for assistants
  const navItems = baseNavItems.filter(item => 
    !item.hideForAssistant || !isAssistant
  );

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rm-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${
          isSidebarOpen ? 'w-64' : 'w-20'
        }`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-slate-100">
          <Link to="/espace-client/dashboard" className="flex items-center gap-3">
            <LogoCockpitPro className="h-10" showText={isSidebarOpen} />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item, index) => {
            if (item.type === 'divider') {
              // Divider de marque (BâtiAssist, Boutique Pro) = style distinct
              if (item.isBrand) {
                return (
                  <div key={`divider-${index}`} className="pt-5 pb-2">
                    {isSidebarOpen && (
                      <div className="px-3 py-1.5 bg-gradient-to-r from-slate-100 to-transparent rounded-lg">
                        <p className="text-sm font-bold text-slate-700">
                          {item.label}
                        </p>
                      </div>
                    )}
                    {!isSidebarOpen && (
                      <div className="mx-3 h-1 bg-gradient-to-r from-ba-primary to-ba-secondary rounded-full" />
                    )}
                  </div>
                );
              }
              // Divider standard (sous-section)
              return (
                <div key={`divider-${index}`} className="pt-4 pb-2">
                  {isSidebarOpen && (
                    <p className="px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                      {item.label}
                    </p>
                  )}
                  {!isSidebarOpen && <hr className="border-slate-200 mx-3" />}
                </div>
              );
            }
            
            const isActive = location.pathname === item.path;
            const isLocked = item.locked && item.module && !isModuleUnlocked(item.module);
            const moduleInfo = item.module ? SUBSCRIPTION_MODULES[item.module] : null;
            
            // Si l'item est verrouillé, rediriger vers la page de présentation du module
            const itemPath = isLocked ? (moduleInfo?.unlockPath || item.path) : item.path;
            
            return (
              <div key={item.path}>
                <Link
                  to={itemPath}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-rm-primary text-white'
                      : isLocked 
                        ? 'text-slate-400 hover:bg-slate-50 cursor-pointer'
                        : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  onClick={isLocked ? (e) => {
                    // Afficher un toast d'info quand on clique sur un item verrouillé
                    toast.info(`${moduleInfo?.name || 'Module'} - ${moduleInfo?.price || 'Abonnement requis'}`, {
                      description: 'Découvre les fonctionnalités et active le module'
                    });
                  } : undefined}
                >
                  <item.icon className={`w-5 h-5 flex-shrink-0 ${isLocked ? 'opacity-50' : ''}`} />
                  {isSidebarOpen && (
                    <>
                      <span className={`font-medium ${isLocked ? 'opacity-70' : ''}`}>{item.label}</span>
                      {isLocked && (
                        <Lock className="w-3.5 h-3.5 ml-auto text-slate-400" />
                      )}
                    </>
                  )}
                  {item.badge && unreadCount > 0 && isSidebarOpen && !isLocked && (
                    <span className="ml-auto bg-rm-secondary text-white text-xs px-2 py-0.5 rounded-full">
                      {unreadCount}
                    </span>
                  )}
                  {item.isOpportunity && opportunitiesCount > 0 && isSidebarOpen && (
                    <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full animate-pulse">
                      {opportunitiesCount}
                    </span>
                  )}
                  {item.showAssistants && assistants.length > 0 && isSidebarOpen && (
                    <span className="ml-auto bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded-full">
                      {assistants.length}
                    </span>
                  )}
                </Link>
                {/* Show assistants list under "Mon équipe" */}
                {item.showAssistants && isSidebarOpen && assistants.length > 0 && (
                  <div className="ml-4 mt-1 space-y-1">
                    {assistants.map((assistant) => (
                      <div
                        key={assistant.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500"
                      >
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-600 text-xs font-medium">
                            {assistant.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="truncate">{assistant.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-rm-primary font-semibold">
                {user.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            {isSidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="font-medium text-rm-text truncate">{user.name}</p>
                <p className="text-xs text-rm-text-secondary truncate">{user.email}</p>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="w-full justify-center gap-2 text-slate-600 hover:text-red-500 hover:border-red-200"
          >
            <LogOut className="w-4 h-4" />
            {isSidebarOpen && 'Deconnexion'}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40 lg:hidden"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
            <motion.aside
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed left-0 top-0 bottom-0 w-[85vw] max-w-[300px] bg-white z-50 lg:hidden flex flex-col shadow-2xl"
            >
              <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <Link to="/espace-client/dashboard" className="flex items-center gap-2">
                  <LogoCockpitPro className="h-8" showText={true} />
                </Link>
                <button 
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-slate-600" />
                </button>
              </div>
              <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
                {navItems.map((item, index) => {
                  if (item.type === 'divider') {
                    // Divider de marque (BâtiAssist, Boutique Pro)
                    if (item.isBrand) {
                      return (
                        <div key={`divider-${index}`} className="pt-4 pb-2">
                          <div className="px-3 py-1.5 bg-gradient-to-r from-slate-100 to-transparent rounded-lg">
                            <p className="text-sm font-bold text-slate-700">
                              {item.label}
                            </p>
                          </div>
                        </div>
                      );
                    }
                    // Divider standard
                    return (
                      <div key={`divider-${index}`} className="pt-3 pb-1">
                        <p className="px-3 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                          {item.label}
                        </p>
                      </div>
                    );
                  }
                  const isActive = location.pathname === item.path;
                  const isLocked = item.locked && item.module && !isModuleUnlocked(item.module);
                  const moduleInfo = item.module ? SUBSCRIPTION_MODULES[item.module] : null;
                  const itemPath = isLocked ? (moduleInfo?.unlockPath || item.path) : item.path;
                  
                  return (
                    <div key={item.path}>
                      <Link
                        to={itemPath}
                        onClick={() => {
                          setIsMobileSidebarOpen(false);
                          if (isLocked) {
                            toast.info(`${moduleInfo?.name || 'Module'} - ${moduleInfo?.price}`, {
                              description: 'Découvre les fonctionnalités et active le module'
                            });
                          }
                        }}
                        className={`flex items-center gap-3 px-3 py-3 rounded-xl transition-all min-h-[48px] ${
                          isActive
                            ? 'bg-rm-primary text-white'
                            : isLocked
                              ? 'text-slate-400 hover:bg-slate-50'
                              : 'text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                        }`}
                      >
                        <item.icon className={`w-5 h-5 flex-shrink-0 ${isLocked ? 'opacity-50' : ''}`} />
                        <span className={`font-medium text-sm ${isLocked ? 'opacity-70' : ''}`}>{item.label}</span>
                        {isLocked && (
                          <Lock className="w-3.5 h-3.5 ml-auto text-slate-400" />
                        )}
                        {item.badge && unreadCount > 0 && !isLocked && (
                          <span className="ml-auto bg-rm-secondary text-white text-xs px-2 py-0.5 rounded-full">
                            {unreadCount}
                          </span>
                        )}
                        {item.showAssistants && assistants.length > 0 && (
                          <span className="ml-auto bg-green-100 text-green-600 text-xs px-2 py-0.5 rounded-full">
                            {assistants.length}
                          </span>
                        )}
                      </Link>
                      {/* Show assistants list under "Mon équipe" on mobile */}
                      {item.showAssistants && assistants.length > 0 && (
                        <div className="ml-4 mt-1 space-y-1">
                          {assistants.map((assistant) => (
                            <div
                              key={assistant.id}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-500"
                            >
                              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-green-600 text-xs font-medium">
                                  {assistant.name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <span className="truncate">{assistant.name}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
              <div className="p-4 border-t border-slate-100">
                {/* Mobile user info */}
                <div className="flex items-center gap-3 mb-3 p-2 bg-slate-50 rounded-lg">
                  <div className="w-10 h-10 bg-rm-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-rm-primary font-semibold">
                      {user.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-rm-text truncate text-sm">{user.name}</p>
                    <p className="text-xs text-rm-text-secondary truncate">{user.email}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={handleLogout}
                  className="w-full justify-center gap-2 min-h-[48px]"
                >
                  <LogOut className="w-4 h-4" />
                  Deconnexion
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-6 py-2.5 sm:py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-slate-100 active:bg-slate-200 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6 text-slate-600" />
            </button>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="hidden lg:flex p-2 hover:bg-slate-100 rounded-lg min-h-[44px] min-w-[44px] items-center justify-center"
            >
              <Menu className="w-5 h-5 text-slate-600" />
            </button>
            {/* Mobile logo */}
            <Link to="/espace-client/dashboard" className="lg:hidden flex items-center">
              <LogoCockpitPro className="h-8 sm:h-9" showText={true} />
            </Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* Client Selector for Assistants */}
            {user?.role === 'assistant' && assignedClients.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setShowClientSelector(!showClientSelector)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors"
                  data-testid="client-selector-btn"
                >
                  <Building className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-green-700 max-w-[120px] truncate">
                    {assignedClients.find(c => c.id === activeClientId)?.name || 'Sélectionner'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-green-600 transition-transform ${showClientSelector ? 'rotate-180' : ''}`} />
                </button>
                {showClientSelector && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[200px] z-50">
                    <div className="px-3 py-2 border-b border-slate-100">
                      <p className="text-xs text-slate-500 font-medium">Gérer le compte de :</p>
                    </div>
                    {assignedClients.map(client => (
                      <button
                        key={client.id}
                        onClick={() => handleClientChange(client.id)}
                        className={`w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 ${
                          client.id === activeClientId ? 'bg-green-50' : ''
                        }`}
                        data-testid={`select-client-${client.id}`}
                      >
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-green-600 text-sm font-medium">
                            {client.name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate">{client.name}</p>
                          <p className="text-xs text-slate-500 truncate">{client.company || client.email}</p>
                        </div>
                        {client.id === activeClientId && (
                          <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <NotificationCenter />
            <div className="hidden sm:flex items-center gap-2 px-2 sm:px-3 py-1.5 bg-slate-50 rounded-lg">
              <div className="w-7 h-7 sm:w-8 sm:h-8 bg-rm-primary/10 rounded-full flex items-center justify-center">
                <span className="text-rm-primary font-semibold text-xs sm:text-sm">
                  {user.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <span className="text-xs sm:text-sm font-medium text-rm-text max-w-[100px] truncate">{user.name}</span>
              <span className="hidden md:inline text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 bg-rm-primary/10 text-rm-primary rounded-full capitalize">
                {user.role}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-x-hidden">
          <Outlet context={{ user, activeClientId, assignedClients, isAssistant: user?.role === 'assistant' }} />
        </main>
      </div>
    </div>
  );
}
